// NPM
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// COMPONENTS
import DateRangeSelector from '../../components/common/DateRangeSelector';
import Paginator from '../../components/common/Paginator';
import MessageReportTableRow from '../../components/reports/messages/MessageReportsTableRow';
import MessageReportSingle from '../../components/reports/messages/MessageReportSingle';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';
import Loader from '../../components/common/Loader';
import Layout from '../../components/layouts/Layout';

// CONFIG
import config from '../../Config';

// NETWORKING
import useMessageReportRequest from '../../requests/MessageReportRequest';

// FORMATTED MESSAGES
import MessageReportsFormattedMessages from '../../messages/reports/MessageReportsFormattedMessages';

// ROUTES
import { ReportsRoutes } from '../../routes/ReportsRoutes';

const MESSAGE_TYPES = ['Binary', 'Red Alert', 'Reminder', 'Reply', 'Standard'];
const SEND_STATUS = [
  'Blacklisted',
  'Cancelled',
  'Delivered',
  'Failed Delivery',
  'Invalid Number',
  'Pending',
  'Processing',
  'Presumed Delivered',
  'Too Many Retries',
];

const MessageReports = ({ accessLevel, accessType, errorHandler }) => {
  const location = useLocation();
  const { getMessageReportData } = useMessageReportRequest();

  const [data, setData] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [timeRange, setTimeRange] = useState(null);
  const [selectedData, setSelectedData] = useState(
    location?.state?.selectedData || null
  );
  const [currentlySearching, setCurrentlySearching] = useState({});
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(0);
  const [reset, setReset] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);

  const [filters, setFilters] = useState({
    fromFilter: '',
    toFilter: '',
    typeFilter: '',
    userFilter: '',
    statusFilter: location?.state?.status || '',
    errorFilter: location?.state?.error_code || '',
  });
  const [filteredText, setFilteredText] = useState([]);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);

  // Add debounce timer ref
  const debounceTimer = useRef(null);

  const handleMessageDataSearch = useCallback(async () => {
    if (fromDate && toDate) {
      try {
        setData([]);
        setIsSearching(true);

        const searchParams = {
          ...filters,
          fromDate,
          toDate,
          timeRange,
          timeRangeStart: fromDate,
          timeRangeEnd: toDate,
          pageNumber: pageNumber,
          pageLength: pageLength,
        };

        const response = await getMessageReportData(searchParams, errorHandler);

        setData(response.finalResults || []);
        setCount(response.rowCount);
        setCurrentlySearching(searchParams);
      } catch (e) {
        console.error(e);
        setData([]);
      } finally {
        setIsSearching(false);
      }
    }
  }, [toDate, fromDate, pageNumber, pageLength, reset]);

  // Add useEffect for initial search
  useEffect(() => {
    handleMessageDataSearch();
  }, [handleMessageDataSearch]);

  /*
    Generates the Search Tools for querying the data
  */
  const generateSearchText = useCallback(() => {
    // Destructures which filters the table is currently displaying fields on.
    const {
      typeFilter,
      userFilter,
      fromFilter,
      toFilter,
      statusFilter,
      errorFilter,
    } = currentlySearching || {};

    const filterText = [];
    const filterValues = ['Type:', 'User:', 'From:', 'To:', 'Status:', 'Error'];

    [
      typeFilter,
      userFilter,
      fromFilter,
      toFilter,
      statusFilter,
      errorFilter,
    ].forEach((filter, i) => {
      if (filter && filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  }, [currentlySearching]);

  useEffect(() => {
    generateSearchText();
  }, [generateSearchText]);

  const handlePageLengthChange = (event) => {
    const newLength = parseInt(event.target.value, 10);

    // Update pagination state with new length and reset page number
    setPageLength(newLength);
    setPageNumber(1);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    // Clear any existing timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set new timer to trigger search after 500ms
    debounceTimer.current = setTimeout(() => {
      setPageNumber(1);
      setReset(!reset);
    }, 500);
  };

  const clearFilters = async () => {
    // Create empty filters
    const emptyFilters = {
      fromFilter: '',
      toFilter: '',
      typeFilter: '',
      userFilter: '',
      statusFilter: '',
      errorFilter: '',
    };

    // Update state
    setFilters(emptyFilters);
    setPageNumber(1);
    setReset(!reset);
  };

  const handleClickToFilter = (name, value) => {
    if (!value) return;

    // Create new filters object
    const newFilters = { ...filters, [name]: value };

    // Update state
    setFilters(newFilters);
    setOpenFilters(true);
    setPageNumber(1);
    setReset(!reset);
  };

  // Open advanced filters if passed to page
  useEffect(() => {
    if (location?.state?.status || location?.state?.error_code)
      setOpenFilters(true);
  }, []);

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  return (
    <Layout
      routes={ReportsRoutes}
      title={
        selectedData
          ? 'Message Details'
          : MessageReportsFormattedMessages.messageReportsTitle
      }
      accessLevel={accessLevel}
      accessType={accessType}
      backButtonText={
        selectedData ? MessageReportsFormattedMessages.backText : null
      }
      handleBackButton={() => setSelectedData(null)}
    >
      {selectedData ? (
        <MessageReportSingle
          selected={selectedData}
          errorHandler={errorHandler}
        />
      ) : (
        <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
          <Grid size={{ xs: 12, lg: 9 }}>
            <DateRangeSelector
              key={selectorKeyRefresh ? 1 : 0}
              handleDateSearch={handleMessageDataSearch}
              parentStartDate={fromDate}
              setParentStartDate={setFromDate}
              parentEndDate={toDate}
              setParentEndDate={setToDate}
            />

            <AdvancedFiltersContainer
              open={openFilters}
              setOpen={setOpenFilters}
            >
              <Box component="form" id="filter-form">
                <Stack
                  sx={{
                    flexDirection: { lg: 'row' },
                    gap: 2,
                    marginY: 2,
                    maxWidth: { lg: 674 },
                  }}
                >
                  <Stack>
                    <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                      By Field:
                    </Typography>
                  </Stack>

                  <Stack sx={{ gap: 1, flex: 1 }}>
                    <Stack
                      sx={{
                        flexDirection: { lg: 'row' },
                        gap: 2,
                        alignItems: { lg: 'center' },
                      }}
                    >
                      <TextField
                        select
                        value={filters.typeFilter}
                        onChange={(e) =>
                          handleFilterChange('typeFilter', e.target.value)
                        }
                        size="small"
                        label="Type"
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>Select Type</em>
                        </MenuItem>
                        {MESSAGE_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        fullWidth
                        size="small"
                        label="User"
                        value={filters.userFilter}
                        onChange={(e) =>
                          handleFilterChange('userFilter', e.target.value)
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="From"
                        value={filters.fromFilter}
                        onChange={(e) =>
                          handleFilterChange('fromFilter', e.target.value)
                        }
                      />
                    </Stack>

                    <Stack>
                      <Stack
                        sx={{
                          flexDirection: { lg: 'row' },
                          gap: 2,
                          alignItems: { lg: 'center' },
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label="To"
                          value={filters.toFilter}
                          onChange={(e) =>
                            handleFilterChange('toFilter', e.target.value)
                          }
                        />

                        <TextField
                          select
                          value={filters.statusFilter}
                          onChange={(e) =>
                            handleFilterChange('statusFilter', e.target.value)
                          }
                          label="Status"
                          fullWidth
                          size="small"
                        >
                          <MenuItem value="">
                            <em>Select Status</em>
                          </MenuItem>
                          {SEND_STATUS.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          fullWidth
                          size="small"
                          label="Error"
                          value={filters.errorFilter}
                          onChange={(e) =>
                            handleFilterChange('errorFilter', e.target.value)
                          }
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            </AdvancedFiltersContainer>

            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                maxWidth: { lg: 674 },
              }}
            >
              {filteredText && filteredText.length > 0 && (
                <Button
                  color="secondary"
                  onClick={clearFilters}
                  sx={{
                    textTransform: 'none',
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  {MessageReportsFormattedMessages.clear}
                </Button>
              )}
            </Stack>
          </Grid>

          <Grid size={12}>
            {isSearching ? (
              <Loader />
            ) : data.length === 0 ? (
              <Typography sx={{ mt: 4 }}>No results found.</Typography>
            ) : (
              <>
                <Stack>
                  {filteredText && filteredText.length > 0 && (
                    <Typography fontSize={12} marginBottom={1}>
                      {filteredText}
                    </Typography>
                  )}
                </Stack>
                <TableContainer
                  sx={{
                    marginBottom: 4,
                    overflowX: 'auto',
                    width: '100%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {MessageReportsFormattedMessages.dateText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.typeText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.userText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.fromText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.toText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.countryText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.statusText}
                        </TableCell>
                        <TableCell>
                          {MessageReportsFormattedMessages.errorText}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((entry, i) => (
                        <MessageReportTableRow
                          key={i}
                          {...entry}
                          clickToFilter={handleClickToFilter}
                          onClickMessage={(entryId) => {
                            setSelectedData({ smsId: entryId });
                          }}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Paginator
                  itemCount={count}
                  changeEvent={handlePageChange}
                  pageLength={pageLength}
                  editFunction={handlePageLengthChange}
                  pageNumber={pageNumber}
                  showTotal={'Total Messages'}
                />
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Layout>
  );
};

export default MessageReports;
